import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#14b8a6'];

export default function BadgeAchievementAnalytics() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [badgeUnlockData, setBadgeUnlockData] = useState([]);
  const [achievementUnlockData, setAchievementUnlockData] = useState([]);
  const [badgeTypeDistribution, setBadgeTypeDistribution] = useState([]);
  const [userProgressData, setUserProgressData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      // Fetch total student count
      const { count: studentCount } = await supabase
        ?.from('user_profiles')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('role', 'student');
      const total = studentCount || 0;
      setTotalUsers(total);

      // Fetch all badges
      const { data: badges } = await supabase?.from('badges')?.select('*')?.order('badge_name');

      // Fetch all achievements
      const { data: achievements } = await supabase?.from('achievements')?.select('*')?.order('name');

      // Fetch user_achievements (unlocked)
      const { data: userAchievements } = await supabase
        ?.from('user_achievements')
        ?.select('achievement_id, user_id');

      // Fetch achievement_badges (earned via goals)
      const { data: achievementBadges } = await supabase
        ?.from('achievement_badges')
        ?.select('user_id, badge_name');

      // Build badge unlock rates
      const badgeUnlockMap = {};
      achievementBadges?.forEach(ab => {
        const key = ab?.badge_name;
        if (key) badgeUnlockMap[key] = (badgeUnlockMap?.[key] || new Set())?.add(ab?.user_id);
      });

      const badgeData = (badges || [])?.map(b => {
        const unlockedSet = badgeUnlockMap?.[b?.badge_name] || new Set();
        const unlocked = unlockedSet?.size;
        const pct = total > 0 ? Math.round((unlocked / total) * 100) : 0;
        return {
          name: b?.badge_name?.length > 18 ? b?.badge_name?.slice(0, 18) + '…' : b?.badge_name,
          fullName: b?.badge_name,
          unlocked,
          total,
          pct,
          type: b?.badge_type,
          xpThreshold: b?.xp_threshold || 0,
        };
      });
      setBadgeUnlockData(badgeData);

      // Badge type distribution
      const typeMap = {};
      (badges || [])?.forEach(b => {
        const t = b?.badge_type || 'Other';
        typeMap[t] = (typeMap?.[t] || 0) + 1;
      });
      setBadgeTypeDistribution(
        Object.entries(typeMap)?.map(([name, value]) => ({ name, value }))
      );

      // Build achievement unlock rates
      const achUnlockMap = {};
      userAchievements?.forEach(ua => {
        const key = ua?.achievement_id;
        if (key) achUnlockMap[key] = (achUnlockMap?.[key] || new Set())?.add(ua?.user_id);
      });

      const achData = (achievements || [])?.map(a => {
        const unlockedSet = achUnlockMap?.[a?.id] || new Set();
        const unlocked = unlockedSet?.size;
        const pct = total > 0 ? Math.round((unlocked / total) * 100) : 0;
        return {
          name: a?.name?.length > 18 ? a?.name?.slice(0, 18) + '…' : a?.name,
          fullName: a?.name,
          unlocked,
          total,
          pct,
          targetType: a?.target_type,
          targetValue: a?.target_value || 0,
        };
      });
      setAchievementUnlockData(achData);

      // User progress toward requirements
      // Fetch all students with xp
      const { data: students } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, xp_points')
        ?.eq('role', 'student')
        ?.order('xp_points', { ascending: false })
        ?.limit(50);

      // Fetch streaks
      const { data: streaks } = await supabase
        ?.from('practice_streaks')
        ?.select('user_id, current_streak');

      const streakMap = {};
      streaks?.forEach(s => { streakMap[s?.user_id] = s?.current_streak || 0; });

      // Fetch quiz completions per user (from quiz_attempts or similar)
      const { data: quizAttempts } = await supabase
        ?.from('quiz_attempts')
        ?.select('user_id');

      const quizMap = {};
      quizAttempts?.forEach(q => {
        quizMap[q?.user_id] = (quizMap?.[q?.user_id] || 0) + 1;
      });

      // Build progress rows — show users who haven't unlocked all badges/achievements
      const unlockedAchievementsByUser = {};
      userAchievements?.forEach(ua => {
        if (!unlockedAchievementsByUser?.[ua?.user_id]) unlockedAchievementsByUser[ua?.user_id] = new Set();
        unlockedAchievementsByUser?.[ua?.user_id]?.add(ua?.achievement_id);
      });

      const progressRows = (students || [])?.map(s => {
        const xp = s?.xp_points || 0;
        const streak = streakMap?.[s?.id] || 0;
        const quizzes = quizMap?.[s?.id] || 0;
        const unlockedAch = unlockedAchievementsByUser?.[s?.id]?.size || 0;
        const totalAch = achievements?.length || 0;
        const lockedAch = totalAch - unlockedAch;

        // Find next badge threshold
        const nextBadge = (badges || [])?.filter(b => b?.xp_threshold > xp)?.sort((a, b) => a?.xp_threshold - b?.xp_threshold)?.[0];

        return {
          id: s?.id,
          name: s?.full_name || s?.email || 'Unknown',
          xp,
          streak,
          quizzes,
          unlockedAch,
          totalAch,
          lockedAch,
          nextBadgeName: nextBadge?.badge_name || '—',
          nextBadgeXp: nextBadge?.xp_threshold || null,
          xpProgress: nextBadge ? Math.min(100, Math.round((xp / nextBadge?.xp_threshold) * 100)) : 100,
        };
      });
      setUserProgressData(progressRows);

    } catch (err) {
      console.error('Error fetching badge analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      const d = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-sm">
          <p className="font-semibold text-foreground mb-1">{d?.fullName || label}</p>
          <p className="text-muted-foreground">Unlocked: <span className="text-foreground font-medium">{d?.unlocked} / {d?.total}</span></p>
          <p className="text-muted-foreground">Rate: <span className="text-primary font-medium">{d?.pct}%</span></p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading analytics…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation user={user} isAuthenticated={true} notifications={[]} />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbTrail />

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-1">Badge & Achievement Analytics</h1>
              <p className="text-sm text-muted-foreground">
                Unlock rates, distribution, and user progress — {totalUsers} student{totalUsers !== 1 ? 's' : ''} total
              </p>
            </div>
            <button
              onClick={fetchAnalyticsData}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card text-sm text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="RefreshCw" size={16} />
              Refresh
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Badges', value: badgeUnlockData?.length, icon: 'Award', color: 'bg-primary' },
              { label: 'Total Achievements', value: achievementUnlockData?.length, icon: 'Trophy', color: 'bg-accent' },
              { label: 'Badge Types', value: badgeTypeDistribution?.length, icon: 'Tag', color: 'bg-purple-600' },
              { label: 'Students Tracked', value: totalUsers, icon: 'Users', color: 'bg-success' },
            ]?.map((card, i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-5 flex items-center gap-4">
                <div className={`w-11 h-11 rounded-lg ${card?.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={card?.icon} size={20} color="white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{card?.value}</p>
                  <p className="text-xs text-muted-foreground">{card?.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Badge Unlock Rates */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-foreground mb-1">Badge Unlock Rates</h2>
            <p className="text-sm text-muted-foreground mb-5">Percentage of students who have unlocked each badge</p>
            {badgeUnlockData?.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">No badge data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={badgeUnlockData} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} angle={-35} textAnchor="end" interval={0} />
                  <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="pct" fill="#6366f1" radius={[4, 4, 0, 0]} name="Unlock Rate %" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Achievement Unlock Rates + Badge Type Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Achievement Unlock Rates */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-bold text-foreground mb-1">Achievement Unlock Rates</h2>
              <p className="text-sm text-muted-foreground mb-5">Percentage of students who have unlocked each achievement</p>
              {achievementUnlockData?.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">No achievement data available</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={achievementUnlockData} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} angle={-35} textAnchor="end" interval={0} />
                    <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="pct" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Unlock Rate %" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Badge Type Distribution */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-bold text-foreground mb-1">Badge Type Distribution</h2>
              <p className="text-sm text-muted-foreground mb-5">Number of badges per type</p>
              {badgeTypeDistribution?.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">No badge type data available</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={badgeTypeDistribution}
                      cx="50%"
                      cy="45%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {badgeTypeDistribution?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} badge${value !== 1 ? 's' : ''}`, name]} />
                    <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* User Progress Table */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-1">User Progress Toward Requirements</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Students' current XP, streak, quizzes completed, and progress toward next badge
            </p>
            {userProgressData?.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">No student data available</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-3 text-muted-foreground font-medium">Student</th>
                      <th className="text-right py-3 px-3 text-muted-foreground font-medium">XP</th>
                      <th className="text-right py-3 px-3 text-muted-foreground font-medium">Streak</th>
                      <th className="text-right py-3 px-3 text-muted-foreground font-medium">Quizzes</th>
                      <th className="text-right py-3 px-3 text-muted-foreground font-medium">Achievements</th>
                      <th className="text-left py-3 px-3 text-muted-foreground font-medium">Next Badge</th>
                      <th className="text-left py-3 px-3 text-muted-foreground font-medium w-40">XP Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userProgressData?.map((row, i) => (
                      <tr key={row?.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">
                              {row?.name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <span className="text-foreground font-medium truncate max-w-[140px]">{row?.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span className="font-semibold text-foreground">{row?.xp?.toLocaleString()}</span>
                          <span className="text-muted-foreground text-xs ml-1">XP</span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span className={`font-semibold ${row?.streak > 0 ? 'text-orange-500' : 'text-muted-foreground'}`}>
                            {row?.streak > 0 ? `🔥 ${row?.streak}` : '—'}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span className="font-semibold text-foreground">{row?.quizzes}</span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span className="text-foreground font-semibold">{row?.unlockedAch}</span>
                          <span className="text-muted-foreground text-xs"> / {row?.totalAch}</span>
                          {row?.lockedAch > 0 && (
                            <span className="ml-1 text-xs text-amber-500">({row?.lockedAch} locked)</span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          {row?.nextBadgeName !== '—' ? (
                            <div>
                              <span className="text-foreground">{row?.nextBadgeName}</span>
                              <span className="text-muted-foreground text-xs ml-1">({row?.nextBadgeXp?.toLocaleString()} XP)</span>
                            </div>
                          ) : (
                            <span className="text-success text-xs font-medium">✓ All badges unlocked</span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          {row?.nextBadgeXp ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-muted rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full transition-all"
                                  style={{ width: `${row?.xpProgress}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground w-8 text-right">{row?.xpProgress}%</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-success/20 rounded-full h-2">
                                <div className="bg-success h-2 rounded-full w-full" />
                              </div>
                              <span className="text-xs text-success w-8 text-right">100%</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
